import { Transition } from "@headlessui/react";
import { Head, useForm } from "@inertiajs/react";
import { ShieldAlert } from "lucide-react";
import { type FormEventHandler, useRef } from "react";

import HeadingSmall from "@/components/heading-small";
import InputError from "@/components/input-error";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AppLayout from "@/layouts/app-layout";
import SettingsLayout from "@/layouts/settings/layout";
import { settingsPasswordPath } from "@/routes";
import type { BreadcrumbItem, SettingsPasswordPageProps } from "@/types";
import { formatDateTimeAR } from "@/utils/date";

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Configuración de contraseña",
    href: settingsPasswordPath(),
  },
];

interface PasswordForm {
  password_challenge: string;
  password: string;
  password_confirmation: string;
}

export default function Password({
  last_password_change,
}: SettingsPasswordPageProps) {
  const passwordInput = useRef<HTMLInputElement>(null);
  const currentPasswordInput = useRef<HTMLInputElement>(null);

  const { data, setData, errors, put, reset, processing, recentlySuccessful } =
    useForm<Required<PasswordForm>>({
      password_challenge: "",
      password: "",
      password_confirmation: "",
    });

  const updatePassword: FormEventHandler = (e) => {
    e.preventDefault();

    put(settingsPasswordPath(), {
      preserveScroll: true,
      onSuccess: () => reset(),
      onError: (errors) => {
        if (errors.password) {
          reset("password", "password_confirmation");
          passwordInput.current?.focus();
        }

        if (errors.password_challenge) {
          reset("password_challenge");
          currentPasswordInput.current?.focus();
        }
      },
    });
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Actualizar contraseña"
            description="Asegúrate de que tu cuenta use una contraseña larga y aleatoria para mantenerte seguro"
          />

          <Alert>
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Última actualización</AlertTitle>
            <AlertDescription>
              {last_password_change
                ? `Tu contraseña fue actualizada por última vez el ${formatDateTimeAR(last_password_change)}.`
                : "No hay un registro de cambios de contraseña todavía."}
            </AlertDescription>
          </Alert>

          <form onSubmit={updatePassword} className="space-y-6">
            <div className="grid gap-2">
              <Label htmlFor="password_challenge">Contraseña actual</Label>

              <Input
                id="password_challenge"
                ref={currentPasswordInput}
                value={data.password_challenge}
                onChange={(e) => setData("password_challenge", e.target.value)}
                type="password"
                className="mt-1 block w-full"
                autoComplete="current-password"
                placeholder="Contraseña actual"
              />

              <InputError message={errors.password_challenge} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="password">Nueva contraseña</Label>

              <Input
                id="password"
                ref={passwordInput}
                value={data.password}
                onChange={(e) => setData("password", e.target.value)}
                type="password"
                className="mt-1 block w-full"
                autoComplete="new-password"
                placeholder="Nueva contraseña"
              />

              <InputError message={errors.password} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="password_confirmation">
                Confirmar contraseña
              </Label>

              <Input
                id="password_confirmation"
                value={data.password_confirmation}
                onChange={(e) =>
                  setData("password_confirmation", e.target.value)
                }
                type="password"
                className="mt-1 block w-full"
                autoComplete="new-password"
                placeholder="Confirmar contraseña"
              />

              <InputError message={errors.password_confirmation} />
            </div>

            <div className="flex items-center gap-4">
              <Button disabled={processing}>Guardar contraseña</Button>

              <Transition
                show={recentlySuccessful}
                enter="transition ease-in-out"
                enterFrom="opacity-0"
                leave="transition ease-in-out"
                leaveTo="opacity-0"
              >
                <p className="text-sm text-neutral-600">Guardado</p>
              </Transition>
            </div>
          </form>
        </div>
      </SettingsLayout>
    </AppLayout>
  );
}
